import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1761395021105-57876136ed49?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Cadastro() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Cadastro — Ana Paula Moraes"; }, []);
  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/membros", { replace: true });
  }, [user, navigate]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError("A senha precisa ter ao menos 6 caracteres.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const u = await register(form);
      toast.success(`Bem-vinda, ${u.name.split(" ")[0]}! Sua conta foi criada.`);
      navigate("/membros", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      {/* Mobile/tablet hero banner */}
      <div className="lg:hidden relative h-44 sm:h-60 overflow-hidden order-1">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(196,143,149,0.55), rgba(184,115,51,0.55))" }} />
        <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-7 text-white">
          <Link to="/" className="brand-logo text-lg" style={{ color: "white" }}>
            <span style={{ color: "white" }}>Ana Paula</span>{" "}
            <span style={{ color: "#FBE9D5", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif" }}>Moraes</span>
          </Link>
          <div>
            <div className="font-display italic text-2xl sm:text-3xl leading-tight">Comece sua jornada hoje.</div>
            <div className="text-[10px] uppercase tracking-[0.32em] opacity-90 mt-2">Cadastro · Área de Membros</div>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-10 sm:py-14 lg:py-20 order-2 lg:order-1">
        <div className="max-w-md w-full mx-auto fade-in-up">
          <Link to="/" className="brand-logo text-2xl mb-8 sm:mb-10 lg:mb-12 hidden sm:inline-block lg:inline-block">
            <span className="ana">Ana Paula</span> <span className="m">Moraes</span>
          </Link>
          <div className="flex items-center gap-2 mb-3">
            <span className="dot-ornament" />
            <span className="eyebrow">Faça parte</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-3 leading-[1.05] tracking-tight" style={{ color: "var(--text-primary)" }}>
            Crie sua <span className="headline-italic-accent">conta</span>
          </h1>
          <p className="text-[15px] mb-8 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            Comece sua jornada de protagonismo agora mesmo.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Nome completo</label>
              <input required autoComplete="name" className="brand-input" value={form.name} onChange={onChange("name")} placeholder="Seu nome" data-testid="register-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>E-mail</label>
              <input type="email" required autoComplete="email" inputMode="email" className="brand-input" value={form.email} onChange={onChange("email")} placeholder="seu@email.com" data-testid="register-email-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Telefone <span className="lowercase tracking-normal" style={{ color: "var(--text-muted)" }}>(opcional)</span></label>
              <input className="brand-input" autoComplete="tel" inputMode="tel" value={form.phone} onChange={onChange("phone")} placeholder="(11) 9 9999-9999" data-testid="register-phone-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-2 block" style={{ color: "var(--copper)" }}>Senha</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required minLength={6}
                  autoComplete="new-password"
                  className="brand-input pr-12"
                  value={form.password}
                  onChange={onChange("password")}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="register-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-[color:var(--bg-warm)] transition-colors" aria-label="Mostrar senha" style={{ color: "var(--text-muted)" }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="text-sm py-3 px-4 rounded-xl" style={{ background: "rgba(220,38,38,0.06)", color: "#b91c1c", border: "1px solid rgba(220,38,38,0.15)" }} data-testid="register-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting} className="btn-primary w-full mt-2" data-testid="register-submit-btn">
              {submitting ? "Criando conta…" : (<>Criar minha conta <ArrowRight size={16} /></>)}
            </button>
          </form>

          <div className="mt-10 pt-6 text-center text-sm" style={{ borderTop: "1px solid var(--border-light)", color: "var(--text-secondary)" }}>
            Já tem conta?{" "}
            <Link to="/login" className="font-medium hover:underline" style={{ color: "var(--copper)" }} data-testid="register-to-login">Entrar</Link>
          </div>
        </div>
      </div>

      {/* Desktop image side */}
      <div className="hidden lg:block relative overflow-hidden order-2">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(196,143,149,0.4), rgba(184,115,51,0.5))" }} />
        <div className="absolute top-12 right-12 text-right">
          <span className="text-[10px] uppercase tracking-[0.32em] font-semibold opacity-90" style={{ color: "white" }}>Edição · 2026</span>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.32em] opacity-80 mb-4">O Convite</div>
          <div className="font-serif text-4xl xl:text-5xl italic leading-[1.05] mb-4 tracking-tight">
            Você não precisa esperar a vida mudar.
          </div>
          <div className="text-base opacity-90">Você pode assumir o comando — agora.</div>
          <div className="flex items-center gap-3 mt-8">
            <div className="w-12 h-px bg-white/60" />
            <span className="text-xs uppercase tracking-[0.28em] opacity-90">Liderança feminina premium</span>
          </div>
        </div>
      </div>
    </div>
  );
}
